import { useCallback, useEffect, useMemo, useState } from 'react';
import { STAGES, type Opportunity, type Stage } from '../data/commercial.config';
import { BACKEND, update } from '../lib/db';
import { useList } from '../lib/useDb';
import { fetchBoard, moveStage, type BoardRow } from '../lib/pipeline';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';
import './pipeline.css';
import { NewOpportunity, LogInteraction } from '../components/CreateForms';

/* =====================================================================================
   Pipeline.

   The board is the default because movement is the question a commercial review actually
   asks. Two things this deliberately surfaces that most CRMs bury:

   1. The four-party structure. A transplant opportunity has a demand-creating centre, a
      purchasing laboratory, a funding commissioner and a procurement body, and they are
      usually different organisations. Showing only "account" loses deals.
   2. Deals blocked on reimbursement rather than on the customer. If a third of the
      pipeline cannot close until funding exists, that is a market access problem wearing
      a sales problem's clothes, and the forecast should say so.
   ===================================================================================== */

const fmtMoney = (n: number, ccy: string) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(n);

/* Indicative rates against sterling, used only for the board's headline totals; Ledger holds
   the real ones. The BASE is the tenant's (brand.baseCurrency), not sterling: a US company's
   board used to read "Converted to GBP" over dollar deals. */
const RATES: Record<string, number> = { GBP: 1, EUR: 0.86, USD: 0.79, CHF: 0.88, SEK: 0.075 };
const BASE = TENANT.brand.baseCurrency ?? 'GBP';
const ROLES = TENANT.vocabulary.buyingRoles;
const toBase = (n: number, ccy: string) => (n * (RATES[ccy] ?? 1)) / (RATES[BASE] ?? 1);

/* pipeline.v_board rows in the shape the board renders. The seed shape is kept as the contract
   so the board, the list, the drawer and the metrics have one code path. */
function fromBoard(r: BoardRow): Opportunity {
  return {
    id: r.id, ref: r.reference ?? '', name: r.name,
    demandAccount: r.demand_account ?? '', buyingAccount: r.buying_account ?? '',
    payerAccount: r.payer_account ?? undefined, procurementAccount: r.procurement_account ?? undefined,
    siteId: r.site_id ?? undefined, country: r.country ?? '',
    stage: (r.stage as Stage), saleType: (r.sale_type ?? 'direct') as Opportunity['saleType'],
    partner: r.partner ?? undefined,
    amount: Number(r.amount ?? 0), currency: r.currency ?? BASE,
    recurringAnnual: r.recurring_annual ?? undefined,
    expectedClose: r.expected_close ?? '', estimatedTestsYr: r.estimated_tests_yr ?? undefined,
    pricePerTest: r.price_per_test ?? undefined,
    champion: r.champion ?? undefined, economicBuyer: r.economic_buyer ?? undefined,
    competitor: r.competitor ?? undefined, incumbentContractEnd: r.incumbent_contract_end ?? undefined,
    blockedOnReimbursement: Boolean(r.blocked_on_reimbursement),
    reimbursementRoute: r.reimbursement_route ?? undefined,
    nextStep: r.next_step ?? '', nextStepDue: r.next_step_due ?? '',
    owner: r.owner ?? '', daysInStage: r.days_in_stage ?? 0, slipped: r.slipped || undefined,
    stageName: r.stage_name,
  } as Opportunity & { stageName?: string };
}

/* Live tenants read the database; the seed path is unchanged for demonstrations. */
function useOpportunities(): [Opportunity[], () => void] {
  const seed = useList<Opportunity>('opportunity');
  const [rows, setRows] = useState<Opportunity[]>([]);
  const reload = useCallback(() => {
    if (BACKEND !== 'supabase') return;
    fetchBoard().then((r) => setRows(r.map(fromBoard)))
      .catch(onFail('The pipeline', () => setRows([])));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return BACKEND === 'supabase' ? [rows, reload] : [seed, () => {}];
}

export function PipelineView() {
  const [creating, setCreating] = useState(false);
  const [OPPORTUNITIES, reload] = useOpportunities();
  /* Stage names are the tenant's (pipeline.stages.name), carried on each board row; the
     STAGES constant is the seed's wording and the fallback. */
  const stageLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of OPPORTUNITIES as (Opportunity & { stageName?: string })[]) {
      if (o.stageName) m.set(o.stage, o.stageName);
    }
    return (key: string) => m.get(key) ?? STAGES.find((s) => s.key === key)?.label ?? key;
  }, [OPPORTUNITIES]);
  const [view, setView] = useState<'board' | 'list'>('board');
  const [showClosed, setShowClosed] = useState(false);
  const [selected, setSelected] = useState<Opportunity | null>(null);

  const open = useMemo(
    () => OPPORTUNITIES.filter((o) => showClosed || (o.stage !== 'won' && o.stage !== 'lost')),
    [showClosed, OPPORTUNITIES],
  );

  const metrics = useMemo(() => {
    const live = OPPORTUNITIES.filter((o) => o.stage !== 'won' && o.stage !== 'lost');
    const gross = live.reduce((a, o) => a + toBase(o.amount, o.currency), 0);
    const weighted = live.reduce((a, o) => {
      const p = STAGES.find((s) => s.key === o.stage)?.probability ?? 0;
      return a + toBase(o.amount, o.currency) * (p / 100);
    }, 0);
    const blocked = live.filter((o) => o.blockedOnReimbursement);
    const blockedValue = blocked.reduce((a, o) => a + toBase(o.amount, o.currency), 0);
    return {
      count: live.length, gross, weighted,
      blockedCount: blocked.length, blockedValue,
      blockedPct: gross ? Math.round((blockedValue / gross) * 100) : 0,
    };
  }, [OPPORTUNITIES]);

  const boardStages = STAGES.filter((s) => showClosed || (s.key !== 'won' && s.key !== 'lost'));

  return (
    <div className="page pipeline">
      <div className="page-head">
        <h1 className="page-title">Pipeline</h1>
        {/* The four parties in the tenant's own words. This used to say "the four parties that
            make a transplant sale: the centre that creates demand, the laboratory that buys",
            to every tenant, including a pharmaceutical one with no laboratory in sight. */}
        <p className="page-sub">
          Opportunities across the four parties to a sale. {ROLES.demand}. {ROLES.buying}.{' '}
          {ROLES.funding}. {ROLES.process}.
        </p>
      </div>

      {OPPORTUNITIES.length === 0 && (
        <p className="module-empty">No opportunities yet. An opportunity here has four parties. {ROLES.demand}. {ROLES.buying}. {ROLES.funding}. {ROLES.process}.</p>
      )}

      <div className="toolbar">
        <div className="seg">
          <button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>Board</button>
          <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>List</button>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show closed
        </label>
        <button className="btn primary" style={{ marginLeft: 'auto' }}
                onClick={() => setCreating(true)}>New opportunity</button>
      </div>

      <div className="metrics">
        <div className="metric card">
          <div className="label">Open opportunities</div>
          <div className="metric-value">{metrics.count}</div>
        </div>
        <div className="metric card">
          <div className="label">Gross pipeline</div>
          <div className="metric-value">{fmtMoney(metrics.gross, BASE)}</div>
          <div className="metric-foot"><span>Converted to {BASE}</span></div>
        </div>
        <div className="metric card">
          <div className="label">Weighted</div>
          <div className="metric-value">{fmtMoney(metrics.weighted, BASE)}</div>
          <div className="metric-foot"><span>By stage probability</span></div>
        </div>
        <div className="metric card blocked-metric">
          <div className="label">Blocked on funding</div>
          <div className="metric-value">{metrics.blockedPct}%</div>
          <div className="metric-foot">
            <span>{metrics.blockedCount} deals · {fmtMoney(metrics.blockedValue, BASE)}</span>
          </div>
        </div>
      </div>

      {metrics.blockedPct >= 30 && (
        <div className="insight card">
          <strong>{metrics.blockedPct}% of pipeline value cannot close until a reimbursement route exists.</strong>
          <span>
            That is a market access problem, not a sales problem. Chasing these harder will not move
            them; opening the route in Atlas will.
          </span>
        </div>
      )}

      {view === 'board' ? (
        <div className="board">
          {boardStages.map((s) => {
            const items = open.filter((o) => o.stage === s.key);
            const value = items.reduce((a, o) => a + toBase(o.amount, o.currency), 0);
            return (
              <div key={s.key} className="col">
                <header className="col-head">
                  <div className="col-title">{stageLabel(s.key)}</div>
                  <div className="col-meta">
                    <span>{items.length}</span>
                    <span className="col-prob">{s.probability}%</span>
                  </div>
                  <div className="col-value">{fmtMoney(value, BASE)}</div>
                </header>
                <div className="col-body">
                  {items.map((o) => (
                    <button key={o.id} className="deal card" onClick={() => setSelected(o)}>
                      <div className="deal-name">{o.name}</div>
                      <div className="deal-account">{o.buyingAccount}</div>
                      <div className="deal-row">
                        <span className="deal-amount">{fmtMoney(o.amount, o.currency)}</span>
                        <span className={`pill ${o.saleType === 'tender' ? 'warn' : o.saleType === 'distributor' ? 'info' : 'mute'}`}>
                          {o.saleType.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="deal-foot">
                        <span className="deal-flag">{o.country}</span>
                        {o.blockedOnReimbursement && <span className="pill risk">no funding route</span>}
                        {o.competitor && <span className="pill mute">vs {o.competitor}</span>}
                      </div>
                    </button>
                  ))}
                  {!items.length && <div className="col-empty">Nothing here</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Opportunity</th><th>Buying account</th><th>Stage</th>
                <th className="num">Value</th><th className="num">Weighted</th>
                <th>Close</th><th>Next step</th><th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {open.map((o) => {
                const p = STAGES.find((s) => s.key === o.stage)?.probability ?? 0;
                return (
                  <tr key={o.id} onClick={() => setSelected(o)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="cell-title">{o.name}</div>
                      <div className="cell-sub mono">{o.ref}</div>
                    </td>
                    <td>
                      <div className="cell-title">{o.buyingAccount}</div>
                      {o.demandAccount !== o.buyingAccount && (
                        <div className="cell-sub">demand: {o.demandAccount}</div>
                      )}
                    </td>
                    <td><span className="pill mute">{stageLabel(o.stage)}</span></td>
                    <td className="num">{fmtMoney(o.amount, o.currency)}</td>
                    <td className="num">{fmtMoney(toBase(o.amount, o.currency) * (p / 100), BASE)}</td>
                    <td className="num">{o.expectedClose}</td>
                    <td>
                      <div className="cell-title">{o.nextStep}</div>
                      <div className="cell-sub">due {o.nextStepDue}</div>
                    </td>
                    <td>
                      {o.blockedOnReimbursement && <span className="pill risk">funding</span>}
                      {o.slipped && <span className="pill warn">slipped</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <DealDrawer o={selected} onClose={() => setSelected(null)} onChanged={reload} stageLabel={stageLabel} />}
      {creating && (
        <NewOpportunity onClose={() => setCreating(false)}
                        onDone={() => { setCreating(false); reload(); }} />
      )}
    </div>
  );
}

function DealDrawer({ o, onClose, onChanged, stageLabel }: {
  o: Opportunity; onClose: () => void; onChanged?: () => void; stageLabel: (key: string) => string;
}) {
  // Was a button with no handler, so the panel offered an action that did nothing.
  const [logging, setLogging] = useState(false);
  const order = STAGES.map((s) => s.key);
  const idx = order.indexOf(o.stage);
  const next = idx >= 0 && idx < order.length - 3 ? STAGES[idx + 1] : null;

  function advance() {
    if (!next) return;
    if (BACKEND === 'supabase') {
      /* Through the database, so stage_history is written and every other member sees it. */
      moveStage(o.id, next.key)
        .then(() => onChanged?.())
        .catch(onFail('Moving the opportunity', () => {}));
    } else {
      update('opportunity', o.id, { stage: next.key, daysInStage: 0 });
    }
    onClose();
  }

  const parties = [
    /* Hints in the tenant's words ("Demand: the prescribing centre"), with the role prefix
       trimmed since the label already says it. */
    { role: 'Creates demand', name: o.demandAccount, hint: ROLES.demand.replace(/^[^:]+:\s*/, '') },
    { role: 'Buys', name: o.buyingAccount, hint: ROLES.buying.replace(/^[^:]+:\s*/, '') },
    { role: 'Funds', name: o.payerAccount, hint: ROLES.funding.replace(/^[^:]+:\s*/, '') },
    { role: 'Procures', name: o.procurementAccount, hint: ROLES.process.replace(/^[^:]+:\s*/, '') },
  ].filter((p) => p.name);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer card" role="dialog" aria-label={o.name}>
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow mono">{o.ref} · {o.country}</div>
            <h2 className="drawer-title display">{o.name}</h2>
            <div className="drawer-tags">
              <span className="pill accent">{stageLabel(o.stage)}</span>
              <span className="pill mute">{o.saleType.replace('_', ' ')}</span>
              {o.blockedOnReimbursement && <span className="pill risk">Blocked on funding</span>}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="drawer-body">
          <section>
            <div className="label">Value</div>
            <div className="big-number">{fmtMoney(o.amount, o.currency)}</div>
            {o.estimatedTestsYr && (
              <div className="drawer-note">
                {o.estimatedTestsYr.toLocaleString()} tests a year at {fmtMoney(o.pricePerTest ?? 0, o.currency)} each.
                Stating the volume basis is what makes the number defensible rather than a guess.
              </div>
            )}
          </section>

          <section>
            <div className="label">The four parties</div>
            <div className="parties">
              {parties.map((p) => (
                <div key={p.role} className="party">
                  <div className="party-role">{p.role}</div>
                  <div className="party-name">{p.name}</div>
                  <div className="party-hint">{p.hint}</div>
                </div>
              ))}
            </div>
            {parties.length < 3 && (
              <div className="drawer-note">
                Only {parties.length} parties identified. In transplant, a deal with no named funder
                is usually a deal that stalls at business case.
              </div>
            )}
          </section>

          {o.blockedOnReimbursement && (
            <section>
              <div className="label">Funding dependency</div>
              <div className="blocked-box">
                <strong>Cannot close until a reimbursement route exists.</strong>
                <p>{o.reimbursementRoute}</p>
              </div>
            </section>
          )}

          <section>
            <div className="label">Position</div>
            <div className="kv-grid">
              {o.champion && <KV k="Champion" v={o.champion} />}
              {o.competitor && <KV k="Incumbent" v={o.competitor} />}
              {o.incumbentContractEnd && <KV k="Contract ends" v={o.incumbentContractEnd} />}
              {o.partner && <KV k="Via partner" v={o.partner} />}
              <KV k="Days in stage" v={String(o.daysInStage)} />
            </div>
          </section>

          <section>
            <div className="label">Next step</div>
            <p className="drawer-text"><strong>{o.nextStep}</strong>, due {o.nextStepDue}.</p>
          </section>

          {o.note && (
            <section>
              <div className="label">Notes</div>
              <p className="drawer-text">{o.note}</p>
            </section>
          )}

          <section>
            <div className="label">Actions</div>
            <div className="drawer-actions">
              <button className="btn primary" disabled={!next} onClick={advance}>
                {next ? `Advance to ${stageLabel(next.key)}` : 'Closed'}
              </button>
              <button className="btn" onClick={() => setLogging(true)}>Log interaction</button>
              {/* Quoting is not built. A control that looks live and does nothing is worse than
                  one that says so, so it is disabled and explains where to go instead. */}
              <button className="btn" disabled
                      title="Quoting is not built yet. Record the commercial terms on the contract in Operations.">
                Create quote
              </button>
            </div>
          </section>
        </div>
      </aside>
      {logging && (
        <LogInteraction opportunityId={String(o.id)} siteName={o.name}
                        onClose={() => setLogging(false)}
                        onDone={() => setLogging(false)} />
      )}
    </>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return <div className="kv"><div className="kv-k">{k}</div><div className="kv-v kv-text">{v}</div></div>;
}

export type { Stage };
